// Hotel Guest Assistant — module screens (Phase B).
import { useState, useEffect } from 'react';
import {
    ChevronLeft, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2,
    ExternalLink, Loader2, Clock, UtensilsCrossed, Shield, Gift, Coins, Moon, ArrowRight,
    Waves, Wine, Shirt, Plus, BookOpen, HeartPulse, Menu as MenuIcon, Info,
    ConciergeBell, Car, Wrench, ShieldCheck, CircleParking, Recycle, Building2, CloudSun,
} from 'lucide-react';
import { tl } from './assistantText';
import { RequestThread } from '../commandCenter/RequestThread';
import PhotoAttach from '../reviewShield/PhotoAttach';

// Approved custom broom icon (housekeeping) — lucide-style strokes + sparkle.
export const BroomIcon = ({ className, ...props }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
        <path d="m13 11 9-9" />
        <path d="M14.6 12.6c.8.8.9 2.1.2 3L10 22l-8-8 6.4-4.8c.9-.7 2.2-.6 3 .2Z" />
        <path d="m6.8 10.4 6.8 6.8" />
        <path d="m5 17 1.4-1.4" />
        <path d="M18.5 16v3M17 17.5h3" strokeWidth="1.5" />
    </svg>
);

// Card surface treatment (Inherit/Frosted/Clear/Solid) — same pattern as
// Review Shield's cardSurfaceClass; screens receive the resolved class.
export const DEFAULT_SURFACE = 'bg-white/5 backdrop-blur-md border border-white/10';

const chipCls = (active) =>
    `px-3 py-2 rounded-full text-sm font-medium border transition-colors ${
        active ? 'bg-emerald-600 border-emerald-500 text-white'
               : 'bg-white/5 border-white/15 text-slate-200 hover:bg-white/10'}`;

export const ScreenShell = ({ title, onBack, tx, children, testId }) => (
    <div className="w-full max-w-md md:max-w-2xl mx-auto" data-testid={testId}>
        <button type="button" onClick={onBack}
            className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white mb-4 px-3 py-1.5 -ml-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            data-testid="assistant-back-btn">
            <ChevronLeft className="w-4 h-4" /> {tx.back}
        </button>
        <h2 className="text-lg sm:text-xl font-semibold text-white mb-4 tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h2>
        {children}
    </div>
);

const SentCard = ({ icon, title, body, tx, onDone, testId, surface, children }) => (
    <div className={`${surface || DEFAULT_SURFACE} rounded-2xl p-6 text-center`} data-testid={testId}>
        {icon}
        <p className="text-white font-semibold text-base mt-3">{title}</p>
        <p className="text-slate-300 text-sm mt-2">{body}</p>
        {children}
        <button type="button" onClick={onDone}
            className="mt-5 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors"
            data-testid="assistant-sent-done-btn">
            {tx.done}
        </button>
    </div>
);

const _fmtShieldTime = (iso) => {
    try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
};

// C4 — guest request progress shields (LOCKED: SENT=blue, PENDING=orange,
// RECEIVED=green). Polls the EXISTING request lifecycle every ~10s until the
// request reaches a terminal state. RECEIVED = acknowledged/seen, NOT resolved.
export const RequestShields = ({ kind, requestId, cardId, token, tx, api }) => {
    const [st, setSt] = useState(null);
    useEffect(() => {
        if (!requestId || !token) return undefined;
        let alive = true; let timer;
        const base = api || (typeof window !== 'undefined' ? window.location.origin : '');
        const poll = async () => {
            try {
                const r = await fetch(`${base}/api/assistant/${cardId}/request-status/${kind}/${requestId}?token=${encodeURIComponent(token)}`);
                if (!alive) return;
                if (r.ok) {
                    const d = await r.json();
                    setSt(d);
                    if (d.terminal) return; // stop polling at terminal (completed/cancelled)
                }
            } catch { /* keep prior state, retry */ }
            if (alive) timer = setTimeout(poll, 10000);
        };
        poll();
        return () => { alive = false; if (timer) clearTimeout(timer); };
    }, [kind, requestId, cardId, token, api]);

    const received = st?.stage === 'received' || st?.stage === 'resolved';
    const resolved = st?.stage === 'resolved' || !!st?.resolved;
    const cancelled = !!st?.cancelled;
    // 4-stage LOCKED progression: SENT(blue) → IN PROGRESS(orange) → RECEIVED(green) → RESOLVED(teal)
    const on = (key) => {
        if (key === 'sent' || key === 'in_progress') return true; // both light at submit
        if (key === 'received') return received;
        if (key === 'resolved') return resolved;
        return false;
    };
    const cfgByKey = {
        sent: { label: tx.shieldSent, color: 'text-sky-400', ring: 'border-sky-400/50 bg-sky-500/10' },
        in_progress: { label: tx.shieldInProgress || tx.shieldPending, color: 'text-amber-400', ring: 'border-amber-400/50 bg-amber-500/10' },
        received: { label: tx.shieldReceived, color: 'text-emerald-400', ring: 'border-emerald-400/50 bg-emerald-500/10' },
        resolved: { label: tx.shieldResolved || tx.shieldCompleted, color: 'text-teal-300', ring: 'border-teal-300/50 bg-teal-500/10' },
    };
    return (
        <div className="mt-5 bg-white/5 border border-white/10 rounded-2xl p-4 text-left"
            data-testid={`request-shields-${kind}`} data-stage={st?.stage || 'loading'}>
            <p className="text-slate-300 text-xs uppercase tracking-widest mb-3 text-center">{tx.requestProgress}</p>
            <div className="flex items-start justify-between gap-1">
                {['sent', 'in_progress', 'received', 'resolved'].map((k) => {
                    const c = cfgByKey[k];
                    const active = on(k);
                    return (
                        <div key={k} className="flex-1 flex flex-col items-center" data-testid={`shield-${k}`} data-active={String(active)}>
                            <span className={`w-10 h-10 rounded-2xl border flex items-center justify-center ${active ? c.ring : 'border-white/10 bg-white/5'}`}>
                                <Shield className={`w-5 h-5 ${active ? c.color : 'text-slate-600'}`} strokeWidth={2}
                                    fill="currentColor" fillOpacity={active ? 0.18 : 0} />
                            </span>
                            <span className={`text-[10px] mt-1.5 font-semibold text-center leading-tight ${active ? 'text-white' : 'text-slate-500'}`}>{c.label}</span>
                        </div>
                    );
                })}
            </div>
            {received && !resolved && st?.received_by && (
                <p className="text-emerald-300 text-sm text-center mt-3" data-testid="shield-received-by">
                    {(tx.receivedByAt || 'Received by {name} — {time}').replace('{name}', st.received_by).replace('{time}', _fmtShieldTime(st.received_at))}
                </p>
            )}
            {resolved && (
                <p className="text-teal-300 text-sm text-center mt-3" data-testid="shield-resolved-by">
                    {st?.resolved_by
                        ? (tx.resolvedByAt || 'Resolved by {name} — {time}').replace('{name}', st.resolved_by).replace('{time}', _fmtShieldTime(st.resolved_at))
                        : (tx.shieldResolved || tx.shieldCompleted)}
                </p>
            )}
            {cancelled && <p className="text-slate-400 text-xs text-center mt-2">{tx.shieldCancelled}</p>}
            <div className="mt-4 pt-3 border-t border-white/10">
                <p className="text-slate-300 text-xs uppercase tracking-widest mb-2 text-center">{tx.messages || 'Messages'}</p>
                <RequestThread myRole="tenant"
                    list={() => fetch(`${(api || (typeof window !== 'undefined' ? window.location.origin : ''))}/api/assistant/${cardId}/requests/${kind}/${requestId}/messages`).then((r) => (r.ok ? r.json() : { messages: [] }))}
                    send={(body) => fetch(`${(api || (typeof window !== 'undefined' ? window.location.origin : ''))}/api/assistant/${cardId}/requests/${kind}/${requestId}/messages`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body }) }).then((r) => r.json())}
                />
            </div>
        </div>
    );
};

export const SecurityScreen = ({ cfg, tx, post, onBack, surface, cardId, token, api }) => {
    const [view, setView] = useState('root');
    const [category, setCategory] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [reqId, setReqId] = useState(null);
    const cats = cfg.categories || [];
    const urgentOn = cfg.urgent_enabled !== false;
    const standardOn = cfg.standard_enabled !== false;

    const sendUrgent = async () => {
        setBusy(true);
        try { const r = await post('security-request', { urgent: true }); setReqId(r?.id || null); setView('urgentSent'); }
        catch { /* expired handled upstream */ }
        finally { setBusy(false); }
    };
    const sendStandard = async () => {
        if (!category) return;
        setBusy(true);
        try { const r = await post('security-request', { urgent: false, category, message: message.trim() || null }); setReqId(r?.id || null); setView('standardSent'); }
        catch { /* expired handled upstream */ }
        finally { setBusy(false); }
    };

    if (view === 'urgentSent') return (
        <ScreenShell title={tx.security} onBack={onBack} tx={tx} testId="assistant-security-screen">
            <SentCard icon={<Shield className="w-10 h-10 text-rose-400 mx-auto" />}
                title={tx.urgentSentTitle} body={tx.urgentSentBody} tx={tx} onDone={onBack}
                testId="assistant-security-urgent-sent">
                {reqId && <RequestShields kind="security" requestId={reqId} cardId={cardId} token={token} tx={tx} api={api} />}
            </SentCard>
        </ScreenShell>
    );
    if (view === 'standardSent') return (
        <ScreenShell title={tx.security} onBack={onBack} tx={tx} testId="assistant-security-screen">
            <SentCard icon={<CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />}
                title={tx.securitySentTitle} body={tx.securitySentBody} tx={tx} onDone={onBack}
                testId="assistant-security-standard-sent">
                {reqId && <RequestShields kind="security" requestId={reqId} cardId={cardId} token={token} tx={tx} api={api} />}
            </SentCard>
        </ScreenShell>
    );

    return (
        <ScreenShell title={tx.security} onBack={onBack} tx={tx} testId="assistant-security-screen">
            {!urgentOn ? null : view === 'confirm' ? (
                <div className="bg-rose-950/60 border border-rose-500/40 rounded-2xl p-5" data-testid="assistant-urgent-confirm">
                    <p className="text-white font-semibold">{tx.urgentConfirmTitle}</p>
                    <p className="text-rose-200 text-sm mt-2">{tx.urgentConfirmBody}</p>
                    <div className="flex gap-3 mt-4">
                        <button type="button" onClick={() => setView('root')}
                            className="flex-1 py-3 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
                            data-testid="assistant-urgent-cancel-btn">{tx.cancel}</button>
                        <button type="button" onClick={sendUrgent} disabled={busy}
                            className="flex-1 py-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold transition-colors disabled:opacity-60"
                            data-testid="assistant-urgent-confirm-btn">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : tx.confirmSend}
                        </button>
                    </div>
                </div>
            ) : (
                <button type="button" onClick={() => setView('confirm')}
                    className="w-full py-4 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg transition-colors"
                    data-testid="assistant-urgent-btn">
                    <AlertTriangle className="w-5 h-5" /> {tx.urgentButton}
                </button>
            )}

            {standardOn && (
            <div className={`mt-6 ${surface || DEFAULT_SURFACE} rounded-2xl p-5`}>
                <p className="text-white font-semibold mb-1">{tx.standardTitle}</p>
                <p className="text-slate-400 text-xs mb-3">{tx.selectCategory}</p>
                <div className="flex flex-wrap gap-2" data-testid="assistant-security-categories">
                    {cats.map((c) => (
                        <button key={c} type="button" onClick={() => setCategory(c)} className={chipCls(category === c)}
                            data-testid={`assistant-security-cat-${c}`}>
                            {tx.secCat[c] || c}
                        </button>
                    ))}
                </div>
                <label className="block text-slate-400 text-xs mt-4 mb-1">{tx.detailsOptional}</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} rows={3}
                    placeholder={tx.detailsPlaceholder}
                    className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-sm p-3 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                    data-testid="assistant-security-message-input" />
                <button type="button" onClick={sendStandard} disabled={!category || busy}
                    className="mt-3 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-50"
                    data-testid="assistant-security-send-btn">
                    {busy ? tx.sending : tx.send}
                </button>
            </div>
            )}
        </ScreenShell>
    );
};

export const HotelInfoScreen = ({ cfg, tx, lang, onBack, openDest, surface }) => {
    const [open, setOpen] = useState(null);
    const sections = cfg.sections || [];
    return (
        <ScreenShell title={tx.hotelInfo} onBack={onBack} tx={tx} testId="assistant-info-screen">
            <div className="space-y-3">
                {sections.map((s, i) => {
                    const id = s.id || String(i);
                    const isOpen = open === id;
                    return (
                        <div key={id} className={`${surface || DEFAULT_SURFACE} rounded-2xl overflow-hidden`}>
                            <button type="button" onClick={() => setOpen(isOpen ? null : id)}
                                className="w-full flex items-center justify-between p-4 text-left"
                                data-testid={`assistant-info-section-${id}`}>
                                <span className="text-white font-medium text-sm">{tl(s, 'title', lang)}</span>
                                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                            </button>
                            {isOpen && (
                                <div className="px-4 pb-4" data-testid={`assistant-info-body-${id}`}>
                                    <p className="text-slate-300 text-sm whitespace-pre-line">{tl(s, 'body', lang)}</p>
                                    {s.hours && (
                                        <p className="text-slate-400 text-xs mt-2 flex items-center gap-1.5">
                                            <Clock className="w-3.5 h-3.5" /> {tx.hours}: {s.hours}
                                        </p>
                                    )}
                                    {s.url && (
                                        <button type="button" onClick={() => openDest(s.url, tl(s, 'title', lang))}
                                            className="inline-flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 text-sm mt-2"
                                            data-testid={`assistant-info-link-${id}`}>
                                            {tx.moreInfo} <ExternalLink className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </ScreenShell>
    );
};

const FoodCard = ({ name, description, hours, url, urlLabel, tx, onOpen, testId, surface }) => (
    <div className={`${surface || DEFAULT_SURFACE} rounded-2xl p-4`} data-testid={testId}>
        <p className="text-white font-semibold text-sm">{name}</p>
        {description && <p className="text-slate-300 text-sm mt-1">{description}</p>}
        {hours && (
            <p className="text-slate-400 text-xs mt-2 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" /> {tx.hours}: {hours}
            </p>
        )}
        {url && (
            <button type="button" onClick={() => onOpen(url, name)}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
                data-testid={`${testId}-link`}>
                {urlLabel} <ExternalLink className="w-3.5 h-3.5" />
            </button>
        )}
    </div>
);

const RestaurantCard = ({ r, lang, tx, openDest, surface }) => {
    const name = tl(r, 'name', lang) || tx.hotelRestaurant;
    const cuisine = tl(r, 'cuisine', lang);
    const desc = tl(r, 'description', lang);
    const pill = 'inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors';
    return (
        <div className={`${surface || DEFAULT_SURFACE} rounded-2xl overflow-hidden`} data-testid={`assistant-restaurant-${r.id}`}>
            {r.photo_url && (
                <div className="w-full shrink-0 overflow-hidden" style={{ height: `${r.photo_size || 140}px` }}>
                    <img src={r.photo_url} alt={name}
                        className="w-full h-full object-cover" data-testid={`assistant-restaurant-photo-${r.id}`} />
                </div>
            )}
            <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-white font-semibold text-base">{name}</p>
                    {r.gift_card && (
                        <span className="inline-flex items-center gap-1 shrink-0 text-[11px] font-semibold text-amber-300 bg-amber-500/15 border border-amber-400/30 rounded-full px-2 py-1"
                            data-testid={`assistant-restaurant-gift-${r.id}`}>
                            <Gift className="w-3 h-3" /> {tx.giftCards}
                        </span>
                    )}
                </div>
                {cuisine && <p className="text-emerald-300/90 text-xs mt-0.5">{cuisine}</p>}
                {desc && <p className="text-slate-300 text-sm mt-1.5">{desc}</p>}
                {r.hours && (
                    <p className="text-slate-400 text-xs mt-2 flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> {tx.hours}: {r.hours}
                    </p>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                    {r.menu_url && (
                        <button type="button" onClick={() => openDest(r.menu_url, name)}
                            className={`${pill} bg-emerald-600 hover:bg-emerald-700 text-white`}
                            data-testid={`assistant-restaurant-menu-${r.id}`}>
                            {tx.viewMenu} <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {r.website_url && (
                        <button type="button" onClick={() => openDest(r.website_url, name)}
                            className={`${pill} bg-white/10 hover:bg-white/20 text-white border border-white/15`}
                            data-testid={`assistant-restaurant-site-${r.id}`}>
                            {tx.visitSite}
                        </button>
                    )}
                    {r.reservation_url && (
                        <button type="button" onClick={() => openDest(r.reservation_url, name)}
                            className={`${pill} bg-white/10 hover:bg-white/20 text-white border border-white/15`}
                            data-testid={`assistant-restaurant-reserve-${r.id}`}>
                            {tx.reserve}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export const FoodScreen = ({ cfg, tx, lang, onBack, openDest, post, surface }) => {
    const restaurants = (cfg.restaurants || []).filter((r) => r.enabled !== false);
    const hr = cfg.hotel_restaurant || {};
    const rs = cfg.room_service || {};
    const out = cfg.outside_restaurants || {};
    const partners = out.partners || [];
    const legacy = restaurants.length === 0;
    return (
        <ScreenShell title={tx.food} onBack={onBack} tx={tx} testId="assistant-food-screen">
            <div className="space-y-3">
                {restaurants.length > 0 && (
                    <div className="space-y-3" data-testid="assistant-restaurants-list">
                        {restaurants.map((r) => (
                            <RestaurantCard key={r.id} r={r} lang={lang} tx={tx} openDest={openDest} surface={surface} />
                        ))}
                    </div>
                )}
                {legacy && hr.enabled !== false && (
                    <FoodCard name={tl(hr, 'name', lang) || tx.hotelRestaurant}
                        description={tl(hr, 'description', lang)} hours={hr.hours} url={hr.url}
                        urlLabel={tx.viewMenu} tx={tx} onOpen={openDest} testId="assistant-food-restaurant" surface={surface} />
                )}
                {rs.enabled !== false && (
                    <>
                        <FoodCard name={tx.roomService} description={tl(rs, 'description', lang)}
                            hours={rs.hours} url={rs.url} urlLabel={tx.viewMenu} tx={tx} onOpen={openDest}
                            testId="assistant-food-roomservice" surface={surface} />
                        {rs.tip_enabled && <TipNotice label={tx.tipRoomService} tx={tx} post={post} recipient="room_service"
                            surface={surface} testId="assistant-tip-roomservice" />}
                    </>
                )}
                {legacy && out.enabled && partners.length > 0 && (
                    <div>
                        <p className="text-slate-400 text-xs uppercase tracking-widest mt-5 mb-3 flex items-center gap-1.5">
                            <UtensilsCrossed className="w-3.5 h-3.5" /> {tx.partners}
                        </p>
                        <div className="space-y-3" data-testid="assistant-food-partners">
                            {partners.map((p, i) => (
                                <FoodCard key={p.id || i} name={p.name}
                                    description={tl(p, 'description', lang)} hours={p.hours} url={p.url}
                                    urlLabel={tx.visitSite} tx={tx} onOpen={openDest}
                                    testId={`assistant-food-partner-${p.id || i}`} />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </ScreenShell>
    );
};

// Tip engine — guest picks $5 / $10 / other amount; the transaction is
// RECORDED server-side with full context (hotel, room, guest, recipient
// function, amount, hotel-local time). No card is charged today.
export const TipNotice = ({ label, tx, post, recipient, testId, surface }) => {
    const [open, setOpen] = useState(false);
    const [choice, setChoice] = useState(null); // 5 | 10 | 'other'
    const [other, setOther] = useState('');
    const [busy, setBusy] = useState(false);
    const [doneAmt, setDoneAmt] = useState(null);
    const [err, setErr] = useState('');
    const amount = choice === 'other' ? parseFloat(other) : choice;
    const canSend = typeof amount === 'number' && !Number.isNaN(amount) && amount >= 1 && amount <= 500;

    const send = async () => {
        if (!canSend || !post) return;
        setBusy(true);
        setErr('');
        try {
            const d = await post('tip', { recipient, amount });
            setDoneAmt(d?.amount ?? amount);
        } catch (e) { if (e.message !== 'expired') setErr(tx.tipError); }
        finally { setBusy(false); }
    };

    if (doneAmt !== null) {
        const amtStr = `$${Number(doneAmt) % 1 === 0 ? Number(doneAmt).toFixed(0) : Number(doneAmt).toFixed(2)}`;
        return (
            <div className="mt-3 bg-emerald-500/10 border border-emerald-400/30 rounded-2xl p-4 text-center"
                data-testid={`${testId}-thanks`}>
                <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto" />
                <p className="text-emerald-300 font-bold mt-1.5">{tx.tipThanksTitle}</p>
                <p className="text-slate-300 text-sm mt-1" data-testid={`${testId}-thanks-body`}>
                    {(tx.tipThanksBody || '').replace('{amount}', amtStr)
                        .replace('{recipient}', (tx.tipRecipient || {})[recipient] || recipient)}
                </p>
            </div>
        );
    }

    return (
        <div className="mt-3">
            <button type="button" onClick={() => setOpen(!open)}
                className="w-full py-3 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 border border-amber-400/30 text-amber-300 font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2"
                data-testid={testId}>
                <Coins className="w-4 h-4" /> {label}
            </button>
            {open && (
                <div className={`mt-2 ${surface || DEFAULT_SURFACE} rounded-2xl p-4`} data-testid={`${testId}-panel`}>
                    <p className="text-slate-300 text-sm mb-3">{tx.tipHowMuch}</p>
                    <div className="flex flex-wrap gap-2">
                        {[5, 10].map((v) => (
                            <button key={v} type="button" onClick={() => setChoice(v)} className={chipCls(choice === v)}
                                data-testid={`${testId}-amt-${v}`}>${v}</button>
                        ))}
                        <button type="button" onClick={() => setChoice('other')} className={chipCls(choice === 'other')}
                            data-testid={`${testId}-amt-other`}>{tx.tipOtherAmount}</button>
                    </div>
                    {choice === 'other' && (
                        <input type="number" min={1} max={500} step="1" value={other}
                            onChange={(e) => setOther(e.target.value)} placeholder={tx.tipAmountPlaceholder}
                            className="mt-2 w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-base text-center py-2.5 px-4 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                            data-testid={`${testId}-other-input`} />
                    )}
                    {err && <p className="text-rose-400 text-xs mt-2 text-center" data-testid={`${testId}-error`}>{err}</p>}
                    <button type="button" onClick={send} disabled={!canSend || busy}
                        className="mt-3 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-50"
                        data-testid={`${testId}-send-btn`}>
                        {busy ? tx.sending : tx.tipSend}
                    </button>
                </div>
            )}
        </div>
    );
};

const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

export const HousekeepingScreen = ({ cfg, tx, post, onBack, surface }) => {
    const [choice, setChoice] = useState(null); // 'HH:MM' preset | 'time'
    const [timeVal, setTimeVal] = useState('');
    const [busy, setBusy] = useState(false);
    const [setUntil, setSetUntil] = useState(null);
    const [setDay, setSetDay] = useState(null);
    // Absolute target times (hotel-local). Evening requests intelligently mean
    // the next morning — the server resolves that, never the guest.
    const ABS = ['09:00', '10:00', '11:00'];
    const canSubmit = choice === 'time' ? TIME_RE.test(timeVal) : ABS.includes(choice);

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        try {
            const until = choice === 'time' ? timeVal : choice;
            const d = await post('dnd', { until });
            setSetUntil(d?.dnd_until || until);
            setSetDay(d?.dnd_until_day || null);
        } catch { /* expired handled upstream */ }
        finally { setBusy(false); }
    };

    if (setUntil) return (
        <ScreenShell title={tx.housekeeping} onBack={onBack} tx={tx} testId="assistant-housekeeping-screen">
            <SentCard icon={<CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />}
                title={tx.dndSetTitle}
                body={`${tx.dndSetBody} ${(tx.dndAtTimes || {})[setUntil] || setUntil}${setDay === 'tomorrow' ? ` (${tx.dndTomorrow})` : ''}.`}
                tx={tx} onDone={onBack} surface={surface}
                testId="assistant-dnd-set" />
            {cfg.tip_enabled && <TipNotice label={tx.tipHousekeeping} tx={tx} post={post} recipient="housekeeping"
                surface={surface} testId="assistant-tip-housekeeping" />}
        </ScreenShell>
    );

    return (
        <ScreenShell title={tx.housekeeping} onBack={onBack} tx={tx} testId="assistant-housekeeping-screen">
            <div className={`${surface || DEFAULT_SURFACE} rounded-2xl p-5`}>
                <p className="text-white font-semibold mb-1 flex items-center gap-2">
                    <Moon className="w-5 h-5 text-sky-300" data-testid="assistant-dnd-moon-icon" />
                    {tx.dndTitle}
                </p>
                <p className="text-slate-400 text-sm mb-4">{tx.dndBody}</p>
                <div className="flex flex-wrap gap-2" data-testid="assistant-dnd-options">
                    {ABS.map((t) => (
                        <button key={t} type="button" onClick={() => setChoice(t)} className={chipCls(choice === t)}
                            data-testid={`assistant-dnd-at-${t}`}>
                            {(tx.dndAtTimes || {})[t] || t}
                        </button>
                    ))}
                    <button type="button" onClick={() => setChoice('time')} className={chipCls(choice === 'time')}
                        data-testid="assistant-dnd-rel-time">
                        {tx.untilTime}
                    </button>
                </div>
                {choice === 'time' && (
                    <input type="time" value={timeVal} onChange={(e) => setTimeVal(e.target.value)}
                        className="mt-3 w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-base p-3 focus:outline-none focus:border-emerald-500"
                        data-testid="assistant-dnd-time-input" />
                )}
                <button type="button" onClick={submit} disabled={!canSubmit || busy}
                    className="mt-4 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-50"
                    data-testid="assistant-dnd-confirm-btn">
                    {busy ? tx.sending : tx.dndConfirm}
                </button>
            </div>
            {cfg.tip_enabled && <TipNotice label={tx.tipHousekeeping} tx={tx} post={post} recipient="housekeeping"
                surface={surface} testId="assistant-tip-housekeeping" />}
        </ScreenShell>
    );
};

export const ConciergeScreen = ({ cfg, tx, post, onBack, surface, cardId, token, api }) => {
    const [category, setCategory] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [reqId, setReqId] = useState(null);
    const [err, setErr] = useState('');
    const cats = cfg.categories || [];

    const submit = async () => {
        const msg = message.trim() || (category ? (tx.conCat[category] || category) : '');
        if (!msg) { setErr(tx.conMessageRequired); return; }
        setErr('');
        setBusy(true);
        try { const r = await post('concierge-request', { category: category || null, message: msg }); setReqId(r?.id || null); setSent(true); }
        catch { /* expired handled upstream */ }
        finally { setBusy(false); }
    };

    if (sent) return (
        <ScreenShell title={tx.concierge} onBack={onBack} tx={tx} testId="assistant-concierge-screen">
            <SentCard icon={<CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />}
                title={tx.conSentTitle} body={tx.conSentBody} tx={tx} onDone={onBack} surface={surface}
                testId="assistant-concierge-sent">
                {reqId && <RequestShields kind="concierge" requestId={reqId} cardId={cardId} token={token} tx={tx} api={api} />}
            </SentCard>
            {cfg.tip_enabled && <TipNotice label={tx.tipConcierge} tx={tx} post={post} recipient="concierge"
                surface={surface} testId="assistant-tip-concierge" />}
        </ScreenShell>
    );

    return (
        <ScreenShell title={tx.concierge} onBack={onBack} tx={tx} testId="assistant-concierge-screen">
            <div className={`${surface || DEFAULT_SURFACE} rounded-2xl p-5`}>
                <p className="text-white font-semibold mb-3">{tx.conciergeTitle}</p>
                <div className="flex flex-wrap gap-2" data-testid="assistant-concierge-categories">
                    {cats.map((c) => (
                        <button key={c} type="button" onClick={() => setCategory(c)} className={chipCls(category === c)}
                            data-testid={`assistant-concierge-cat-${c}`}>
                            {tx.conCat[c] || c}
                        </button>
                    ))}
                </div>
                <label className="block text-slate-400 text-xs mt-4 mb-1">{tx.conMessage}</label>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={500} rows={3}
                    placeholder={tx.conMessagePlaceholder}
                    className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-sm p-3 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                    data-testid="assistant-concierge-message-input" />
                {err && <p className="text-rose-400 text-xs mt-1" data-testid="assistant-concierge-error">{err}</p>}
                <button type="button" onClick={submit} disabled={busy}
                    className="mt-3 w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors disabled:opacity-60"
                    data-testid="assistant-concierge-send-btn">
                    {busy ? tx.sending : tx.send}
                </button>
            </div>
            {cfg.tip_enabled && <TipNotice label={tx.tipConcierge} tx={tx} post={post} recipient="concierge"
                surface={surface} testId="assistant-tip-concierge" />}
        </ScreenShell>
    );
};

// ─── Guest Services Entry Context: Pool & Terrace ───────────────────────────
// Contextual extension of Guest Services. Reuses the EXISTING request pipeline
// (post → /pool-request & /security-request) so everything flows through the
// same lifecycle + staff queue + Guest Activity. NO payment, NO guest amount,
// NO lounger/table question (the physical tap card = the location).
const PoolSent = ({ tx, onBack, surface, testId, kind, reqId, cardId, token, api }) => (
    <SentCard icon={<CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />}
        title={tx.poolSentTitle} body={tx.poolSentBody} tx={tx} onDone={onBack} surface={surface} testId={testId}>
        {kind && reqId && <RequestShields kind={kind} requestId={reqId} cardId={cardId} token={token} tx={tx} api={api} />}
    </SentCard>
);

// Combined Food + Drink order builder (browse both menus, one order, PIN to submit).
const PoolOrderBuilder = ({ pool, tx, post, onBack, surface, cardId, token, api, startTab }) => {
    const [tab, setTab] = useState(startTab || 'drinks');
    const [order, setOrder] = useState('');
    const [notes, setNotes] = useState('');
    const [step, setStep] = useState('build'); // build | review | pin | sent
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [reqId, setReqId] = useState(null);
    const drinkImg = pool.drink_menu_image_url;
    const foodImg = pool.food_menu_image_url;

    const submit = async () => {
        if (!/^\d{4}$/.test(pin)) { setErr(tx.poolPinError); return; }
        setErr(''); setBusy(true);
        try {
            const r = await post('pool-request', { service: 'order', message: order.trim(), notes: notes.trim() || null, pin });
            setReqId(r?.id || null); setStep('sent');
        } catch (e) { setErr(tx.poolPinError); }
        finally { setBusy(false); }
    };

    if (step === 'sent') return (
        <ScreenShell title={tx.poolReview} onBack={onBack} tx={tx} testId="assistant-pool-order-screen">
            <PoolSent tx={tx} onBack={onBack} surface={surface} testId="assistant-pool-order-sent"
                kind="concierge" reqId={reqId} cardId={cardId} token={token} api={api} />
        </ScreenShell>
    );

    if (step === 'review' || step === 'pin') return (
        <ScreenShell title={tx.poolReview} onBack={() => setStep('build')} tx={tx} testId="assistant-pool-review-screen">
            <div className={`${surface || DEFAULT_SURFACE} rounded-2xl p-5 space-y-4`}>
                <div>
                    <p className="text-slate-400 text-xs mb-1">{tx.poolYourOrder}</p>
                    <p className="text-white text-sm whitespace-pre-wrap" data-testid="assistant-pool-review-order">{order || '—'}</p>
                </div>
                {notes && (<div><p className="text-slate-400 text-xs mb-1">{tx.poolNotes}</p><p className="text-white text-sm">{notes}</p></div>)}
                {step === 'review' ? (
                    <div className="flex gap-2">
                        <button type="button" onClick={() => setStep('build')} className="flex-1 py-3 rounded-xl border border-white/20 text-white text-sm" data-testid="assistant-pool-edit-btn">{tx.back}</button>
                        <button type="button" onClick={() => setStep('pin')} className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="assistant-pool-confirm-btn">{tx.poolConfirmPlace}</button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <label className="block text-slate-300 text-sm">{tx.poolPinPrompt}</label>
                        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} inputMode="numeric" maxLength={4}
                            className="w-full text-center tracking-[0.5em] text-2xl rounded-xl bg-slate-900/60 border border-white/15 text-white p-3 focus:outline-none focus:border-emerald-500"
                            data-testid="assistant-pool-pin-input" placeholder="••••" />
                        {err && <p className="text-rose-400 text-xs" data-testid="assistant-pool-pin-error">{err}</p>}
                        <button type="button" onClick={submit} disabled={busy} className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-60" data-testid="assistant-pool-submit-btn">{busy ? tx.sending : tx.poolConfirmPlace}</button>
                    </div>
                )}
            </div>
        </ScreenShell>
    );

    const img = tab === 'drinks' ? drinkImg : foodImg;
    return (
        <ScreenShell title={tx.poolTitle} onBack={onBack} tx={tx} testId="assistant-pool-order-build-screen">
            <div className={`${surface || DEFAULT_SURFACE} rounded-2xl p-5 space-y-4`}>
                <div className="flex gap-2">
                    <button type="button" onClick={() => setTab('drinks')} className={`flex-1 py-2 rounded-xl text-sm font-semibold ${tab === 'drinks' ? 'bg-teal-600 text-white' : 'bg-slate-900/50 text-slate-300 border border-white/10'}`} data-testid="assistant-pool-tab-drinks">{tx.poolDrinks}</button>
                    <button type="button" onClick={() => setTab('food')} className={`flex-1 py-2 rounded-xl text-sm font-semibold ${tab === 'food' ? 'bg-amber-600 text-white' : 'bg-slate-900/50 text-slate-300 border border-white/10'}`} data-testid="assistant-pool-tab-food">{tx.poolFood}</button>
                </div>
                {img ? (
                    <img src={img} alt={tx.poolMenuLabel} className="w-full rounded-xl border border-white/10" data-testid={`assistant-pool-${tab}-menu-image`} />
                ) : (<p className="text-slate-400 text-sm text-center py-4">{tx.poolMenuLabel}</p>)}
                <div>
                    <label className="block text-slate-400 text-xs mb-1">{tx.poolYourOrder}</label>
                    <textarea value={order} onChange={(e) => setOrder(e.target.value)} rows={3} maxLength={800}
                        placeholder={tx.poolOrderPlaceholder}
                        className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-sm p-3 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                        data-testid="assistant-pool-order-input" />
                </div>
                <div>
                    <label className="block text-slate-400 text-xs mb-1">{tx.poolNotes}</label>
                    <input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={200}
                        className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-sm p-3 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                        data-testid="assistant-pool-order-notes" placeholder={tx.poolNotes} />
                </div>
                <div className="flex gap-2">
                    <button type="button" onClick={() => setTab(tab === 'drinks' ? 'food' : 'drinks')} className="flex-1 py-3 rounded-xl border border-white/20 text-white text-sm" data-testid="assistant-pool-continue-btn">{tab === 'drinks' ? tx.poolContinueFood : tx.poolContinueDrink}</button>
                    <button type="button" onClick={() => setStep('review')} disabled={!order.trim()} className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-50" data-testid="assistant-pool-review-btn">{tx.poolReview}</button>
                </div>
            </div>
        </ScreenShell>
    );
};

// Simple service requests — NO passcode.
const PoolSimpleService = ({ service, tx, post, onBack, surface }) => {
    const [notes, setNotes] = useState('');
    const [msg, setMsg] = useState('');
    const [towelCount, setTowelCount] = useState(1);
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState('');
    const title = { towels: tx.poolTowels, magazine: tx.poolMagazine, other: tx.poolOtherSvc }[service];
    const submit = async () => {
        if (service === 'other' && !msg.trim()) { setErr(tx.conMessageRequired); return; }
        setErr(''); setBusy(true);
        try {
            await post('pool-request', { service, message: msg.trim() || null, notes: notes.trim() || null, towel_count: service === 'towels' ? towelCount : null });
            setSent(true);
        } catch (e) { /* handled upstream */ }
        finally { setBusy(false); }
    };
    if (sent) return (
        <ScreenShell title={title} onBack={onBack} tx={tx} testId={`assistant-pool-${service}-screen`}>
            <PoolSent tx={tx} onBack={onBack} surface={surface} testId={`assistant-pool-${service}-sent`} />
        </ScreenShell>
    );
    return (
        <ScreenShell title={title} onBack={onBack} tx={tx} testId={`assistant-pool-${service}-screen`}>
            <div className={`${surface || DEFAULT_SURFACE} rounded-2xl p-5 space-y-4`}>
                {service === 'towels' && (
                    <div>
                        <label className="block text-slate-400 text-xs mb-2">{tx.poolTowelsQty}</label>
                        <div className="flex gap-2">
                            {[1, 2].map((n) => (
                                <button key={n} type="button" onClick={() => setTowelCount(n)} className={`flex-1 py-3 rounded-xl font-semibold ${towelCount === n ? 'bg-emerald-600 text-white' : 'bg-slate-900/50 text-slate-300 border border-white/10'}`} data-testid={`assistant-pool-towel-qty-${n}`}>{n}</button>
                            ))}
                        </div>
                    </div>
                )}
                {service === 'other' && (
                    <div>
                        <label className="block text-slate-400 text-xs mb-1">{tx.poolOtherLabel}</label>
                        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} maxLength={500}
                            className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-sm p-3 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                            data-testid="assistant-pool-other-input" placeholder={tx.poolOtherPlaceholder} />
                    </div>
                )}
                <div>
                    <label className="block text-slate-400 text-xs mb-1">{tx.poolNotes}</label>
                    <input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={200}
                        className="w-full rounded-xl bg-slate-900/60 border border-white/15 text-white text-sm p-3 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                        data-testid={`assistant-pool-${service}-notes`} placeholder={tx.poolNotes} />
                </div>
                {err && <p className="text-rose-400 text-xs">{err}</p>}
                <button type="button" onClick={submit} disabled={busy} className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-60" data-testid={`assistant-pool-${service}-send-btn`}>{busy ? tx.sending : tx.send}</button>
            </div>
        </ScreenShell>
    );
};

// Security / Medical — reuses the EXISTING security-request infrastructure. No new PIN.
const PoolSOS = ({ tx, post, onBack, surface, cardId, token, api }) => {
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(null); // 'security' | 'medical'
    const [reqId, setReqId] = useState(null);
    const send = async (kind) => {
        setBusy(true);
        try {
            const r = await post('security-request', { urgent: true, category: kind });
            setReqId(r?.id || null); setSent(kind);
        } catch (e) { /* handled upstream */ }
        finally { setBusy(false); }
    };
    if (sent) return (
        <ScreenShell title={sent === 'security' ? tx.poolSecurity : tx.poolMedical} onBack={onBack} tx={tx} testId="assistant-pool-sos-sent-screen">
            <PoolSent tx={tx} onBack={onBack} surface={surface} testId="assistant-pool-sos-sent"
                kind="security" reqId={reqId} cardId={cardId} token={token} api={api} />
        </ScreenShell>
    );
    return (
        <ScreenShell title={tx.poolSecurityMedical} onBack={onBack} tx={tx} testId="assistant-pool-sos-screen">
            <p className="text-slate-300 text-sm mb-4 text-center">{tx.poolChoose}</p>
            <div className="space-y-3">
                <button type="button" onClick={() => send('security')} disabled={busy}
                    className="w-full flex items-center gap-3 p-5 rounded-2xl bg-red-600/90 hover:bg-red-600 text-white text-left disabled:opacity-60"
                    data-testid="assistant-pool-security-btn">
                    <Shield className="w-8 h-8 shrink-0" />
                    <div><p className="font-bold text-lg">{tx.poolSecurity}</p><p className="text-white/80 text-xs">{tx.poolSecurityDesc}</p></div>
                </button>
                <button type="button" onClick={() => send('medical')} disabled={busy}
                    className="w-full flex items-center gap-3 p-5 rounded-2xl bg-yellow-400/95 hover:bg-yellow-400 text-slate-900 text-left disabled:opacity-60"
                    data-testid="assistant-pool-medical-btn">
                    <HeartPulse className="w-8 h-8 shrink-0" />
                    <div><p className="font-bold text-lg">{tx.poolMedical}</p><p className="text-slate-800 text-xs">{tx.poolMedicalDesc}</p></div>
                </button>
            </div>
        </ScreenShell>
    );
};

export const PoolTerraceScreen = ({ pool, tx, post, surface, cardId, token, api, roomNumber, onChangeRoom, onFullMenu }) => {
    const [view, setView] = useState('root');
    const p = pool || {};
    if (view === 'order') return <PoolOrderBuilder pool={p} tx={tx} post={post} onBack={() => setView('root')} surface={surface} cardId={cardId} token={token} api={api} startTab="drinks" />;
    if (view === 'order_food') return <PoolOrderBuilder pool={p} tx={tx} post={post} onBack={() => setView('root')} surface={surface} cardId={cardId} token={token} api={api} startTab="food" />;
    if (['towels', 'magazine', 'other'].includes(view)) return <PoolSimpleService service={view} tx={tx} post={post} onBack={() => setView('towelsmenu')} surface={surface} />;
    if (view === 'sos') return <PoolSOS tx={tx} post={post} onBack={() => setView('root')} surface={surface} cardId={cardId} token={token} api={api} />;
    if (view === 'towelsmenu') return (
        <ScreenShell title={tx.poolTowelsOther} onBack={() => setView('root')} tx={tx} testId="assistant-pool-towelsmenu-screen">
            <div className="grid grid-cols-1 gap-3">
                {[{ k: 'towels', i: <Shirt className="w-6 h-6" />, t: tx.poolTowels }, { k: 'magazine', i: <BookOpen className="w-6 h-6" />, t: tx.poolMagazine }, { k: 'other', i: <Plus className="w-6 h-6" />, t: tx.poolOtherSvc }].map((s) => (
                    <button key={s.k} type="button" onClick={() => setView(s.k)} className={`${surface || DEFAULT_SURFACE} rounded-2xl p-4 flex items-center gap-3 text-white hover:opacity-90`} data-testid={`assistant-pool-sub-${s.k}`}>
                        <span className="text-teal-300">{s.i}</span><span className="font-semibold">{s.t}</span>
                    </button>
                ))}
            </div>
        </ScreenShell>
    );

    const tiles = [
        { key: 'order', on: p.drinks !== false, icon: <Wine className="w-6 h-6 sm:w-7 sm:h-7" />, title: tx.poolDrinks, tag: tx.poolDrinksTag, accent: 'teal' },
        { key: 'order_food', on: p.food !== false, icon: <UtensilsCrossed className="w-6 h-6 sm:w-7 sm:h-7" />, title: tx.poolFood, tag: tx.poolFoodTag, accent: 'amber' },
        { key: 'towelsmenu', on: true, icon: <Shirt className="w-6 h-6 sm:w-7 sm:h-7" />, title: tx.poolTowelsOther, tag: tx.poolTowelsOtherTag, accent: 'green' },
        { key: 'sos', on: true, icon: <Shield className="w-6 h-6 sm:w-7 sm:h-7" />, title: tx.poolSecurityMedical, tag: tx.poolSecurityMedicalTag, accent: 'rose' },
    ].filter((t) => t.on);
    return (
        <div className="w-full max-w-md md:max-w-3xl lg:max-w-4xl mx-auto" data-testid="assistant-pool-screen">
            <div className="flex items-center justify-between mb-3">
                <p className="text-slate-300 text-sm" data-testid="assistant-pool-room-label">{tx.room} <span className="text-white font-semibold">{roomNumber}</span></p>
                {onChangeRoom && (<button type="button" onClick={onChangeRoom} className="text-xs text-slate-400 hover:text-white underline underline-offset-2" data-testid="assistant-pool-change-room-btn">{tx.changeRoom}</button>)}
            </div>
            <div className="flex items-center gap-2 mb-1"><Waves className="w-5 h-5 text-teal-300" /><h2 className="text-lg sm:text-xl font-semibold text-white tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>{tx.poolTitle}</h2></div>
            <p className="text-slate-400 text-xs mb-5">{tx.poolTag}</p>
            <div className="grid grid-cols-2 gap-3 items-stretch" data-testid="assistant-pool-menu">
                {tiles.map((t) => (
                    <ServiceTile key={t.key} icon={t.icon} title={t.title} tag={t.tag} accent={t.accent} surface={surface} onClick={() => setView(t.key)} testId={`assistant-pool-tile-${t.key}`} />
                ))}
            </div>
            <button type="button" onClick={onFullMenu} className="mt-5 w-full py-4 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold flex items-center justify-center gap-2 transition-colors" data-testid="assistant-pool-full-menu-btn">
                <MenuIcon className="w-5 h-5" /> {tx.poolFullMenu}
            </button>
        </div>
    );
};

// Property Experience — configurable Main Tile → Sub-Tile → EXISTING request engine.
// Trilingual (tl). Reuses concierge-request lifecycle + hotel_feedback_photos.
// Renders ONLY when the property config defines tiles (Hotel = [] → never shown).
export const PropertyTilesScreen = ({ tile, tx, lang, post, onBack, surface, cardId, token, api }) => {
    const [sub, setSub] = useState(null);
    const [notes, setNotes] = useState('');
    const [photos, setPhotos] = useState([]);
    const [busy, setBusy] = useState(false);
    const [reqId, setReqId] = useState(null);
    const [err, setErr] = useState('');
    if (!tile) return null;
    const label = (o, base) => tl(o, base, lang) || o?.[`${base}_en`] || '';

    const submit = async () => {
        if (!sub) return;
        setBusy(true); setErr('');
        try {
            let photo_ids = [];
            if (sub.photo_enabled && photos.length) {
                const fd = new FormData();
                fd.append('token', token);
                photos.forEach((f) => fd.append('photos', f));
                const r = await fetch(`${api}/api/assistant/${cardId}/request-photos`, { method: 'POST', body: fd });
                if (r.ok) { const d = await r.json(); photo_ids = d.photo_ids || []; }
            }
            const msg = `${label(tile, 'label')} — ${label(sub, 'label')}${notes.trim() ? `: ${notes.trim()}` : ''}`;
            const res = await post('concierge-request', {
                category: tile.id, subcategory: sub.id, message: msg, photo_ids,
            });
            setReqId(res?.id || null);
        } catch (e) { setErr(tx.error || 'Something went wrong'); }
        setBusy(false);
    };

    if (reqId) return (
        <ScreenShell title={label(tile, 'label')} onBack={onBack} tx={tx} testId="property-tile-sent-screen">
            <div className="flex flex-col items-center text-center py-8" data-testid="property-tile-success">
                <CheckCircle2 className="w-14 h-14 text-emerald-400 mb-4" />
                <p className="text-white font-semibold">{tx.requestSent || 'Request submitted'}</p>
                <p className="text-slate-300 text-sm mt-1">{label(sub, 'label')}</p>
            </div>
        </ScreenShell>
    );

    if (sub) return (
        <ScreenShell title={label(sub, 'label')} onBack={() => setSub(null)} tx={tx} testId="property-subtile-form-screen">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder={tx.detailsPlaceholder || ''} rows={3}
                className="w-full rounded-xl bg-white/5 border border-white/10 p-3 text-white text-sm mb-4"
                data-testid="property-tile-notes" />
            {sub.photo_enabled && <PhotoAttach photos={photos} onChange={setPhotos}
                helperText={tx.propertyPhotoHelper} />}
            {err && <p className="text-rose-400 text-sm mt-2" data-testid="property-tile-error">{err}</p>}
            <button type="button" onClick={submit} disabled={busy}
                className="mt-4 w-full rounded-xl bg-teal-500 hover:bg-teal-400 text-white font-semibold py-3 disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="property-tile-submit">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (tx.submit || 'Submit')}
            </button>
        </ScreenShell>
    );

    const subs = (tile.subtiles || []).filter((s) => s.enabled !== false);
    return (
        <ScreenShell title={label(tile, 'label')} onBack={onBack} tx={tx} testId="property-tile-screen">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="property-subtile-list">
                {subs.map((s) => (
                    <button key={s.id} type="button" onClick={() => { setSub(s); setNotes(''); setPhotos([]); setErr(''); }}
                        className={`${surface} rounded-2xl p-4 text-left flex items-center justify-between hover:bg-white/10 transition-colors`}
                        data-testid={`property-subtile-${s.id}`}>
                        <span className="text-white text-sm font-medium">{label(s, 'label')}</span>
                        <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                    </button>
                ))}
            </div>
        </ScreenShell>
    );
};



export const MenuTile = ({ icon, title, tag, onClick, href, accent, badge, testId, surface }) => {
    const inner = (
        <>
            <div className={`w-12 h-12 rounded-full border flex items-center justify-center shrink-0 bg-gradient-to-br ${accent || 'from-emerald-500/25 to-emerald-900/5 border-emerald-400/50 text-emerald-400 shadow-[0_0_14px_rgba(16,185,129,0.2)]'}`}>
                {icon}
            </div>
            <div className="flex-1 min-w-0 text-left">
                <p className="text-white font-semibold text-sm">{title}</p>
                <p className="text-slate-400 text-xs mt-0.5 truncate">{tag}</p>
            </div>
            {badge > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center shrink-0"
                    data-testid={`${testId}-badge`}>{badge}</span>
            )}
            {href ? <ExternalLink className="w-4 h-4 text-slate-500 shrink-0" />
                  : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
        </>
    );
    const cls = `w-full flex items-center gap-3 ${surface || DEFAULT_SURFACE} hover:bg-white/10 rounded-2xl p-4 transition-colors`;
    if (href) return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testId}>{inner}</a>
    );
    return (
        <button type="button" onClick={onClick} className={cls} data-testid={testId}>{inner}</button>
    );
};

// Guest main-menu service tile — responsive design system (2-col mobile grid,
// expands on tablet/desktop). Uses the approved icon set + accent glow rings.
const TILE_ACCENTS = {
    rose: { ring: 'border-rose-500/70 text-rose-400 shadow-[0_0_18px_rgba(244,63,94,0.28)]', arrow: 'text-rose-400' },
    green: { ring: 'border-green-400/70 text-green-400 shadow-[0_0_18px_rgba(74,222,128,0.25)]', arrow: 'text-green-400' },
    amber: { ring: 'border-amber-400/70 text-amber-400 shadow-[0_0_18px_rgba(251,191,36,0.25)]', arrow: 'text-amber-400' },
    purple: { ring: 'border-purple-400/70 text-purple-400 shadow-[0_0_18px_rgba(192,132,252,0.28)]', arrow: 'text-purple-400' },
    yellow: { ring: 'border-yellow-400/70 text-yellow-400 shadow-[0_0_18px_rgba(250,204,21,0.25)]', arrow: 'text-yellow-400' },
    teal: { ring: 'border-teal-400/70 text-teal-300 shadow-[0_0_18px_rgba(45,212,191,0.25)]', arrow: 'text-teal-300' },
    violet: { ring: 'border-violet-400/70 text-violet-400 shadow-[0_0_18px_rgba(167,139,250,0.28)]', arrow: 'text-violet-400' },
    orange: { ring: 'border-orange-400/70 text-orange-400 shadow-[0_0_18px_rgba(251,146,60,0.25)]', arrow: 'text-orange-400' },
    blue: { ring: 'border-sky-400/70 text-sky-400 shadow-[0_0_18px_rgba(56,189,248,0.28)]', arrow: 'text-sky-400' },
    cyan: { ring: 'border-cyan-400/70 text-cyan-300 shadow-[0_0_18px_rgba(34,211,238,0.28)]', arrow: 'text-cyan-300' },
    pink: { ring: 'border-pink-400/70 text-pink-400 shadow-[0_0_18px_rgba(244,114,182,0.28)]', arrow: 'text-pink-400' },
    emerald: { ring: 'border-emerald-400/70 text-emerald-400 shadow-[0_0_18px_rgba(16,185,129,0.25)]', arrow: 'text-emerald-400' },
};

export const ServiceTile = ({ icon, title, tag, onClick, accent, badge, testId, surface, compact = false, statusColor }) => {
    const a = TILE_ACCENTS[accent] || TILE_ACCENTS.emerald;
    return (
        <button type="button" onClick={onClick}
            className={`relative h-full flex flex-col items-center text-center ${surface || DEFAULT_SURFACE} hover:bg-white/10 rounded-2xl transition-colors ${compact ? 'px-2.5 pt-3 pb-3' : 'px-3 pt-4 pb-3.5'}`}
            data-testid={testId}>
            {badge > 0 && (
                <span className="absolute top-2.5 right-2.5 min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center"
                    data-testid={`${testId}-badge`}>{badge}</span>
            )}
            <div className={`relative rounded-full border-2 bg-slate-950/40 flex items-center justify-center ${a.ring} ${compact ? 'w-11 h-11 sm:w-12 sm:h-12' : 'w-14 h-14 sm:w-16 sm:h-16'}`}>
                {icon}
                {statusColor && (
                    <span data-testid={`${testId}-status`} data-status={statusColor}
                        className={`absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full ring-2 ring-slate-900 ${_DOT_CLS[statusColor] || 'bg-slate-500'}`} />
                )}
            </div>
            <p className={`text-white font-semibold text-sm leading-snug ${compact ? 'mt-2' : 'mt-3'}`}>{title}</p>
            <p className={`text-slate-400 text-[11px] leading-snug mt-1 line-clamp-2 flex-1`}>{tag}</p>
            <ArrowRight className={`w-4 h-4 ${compact ? 'mt-1.5' : 'mt-2'} ${a.arrow}`} aria-hidden="true" />
        </button>
    );
};

// Map an activity item to the guest/resident SERVICE it belongs to (so the
// matching menu tile can show a status dot). Reuses existing kind/category.
export const itemService = (it, isCondo) => {
    if (!it) return null;
    if (it.kind === 'valet') return 'valet';
    if (it.kind === 'security') return 'security';
    if (it.kind === 'concierge') {
        const c = (it.category || '').toLowerCase();
        if (c === 'housekeeping') return 'housekeeping';
        if (['food', 'room_service', 'dining', 'in_room_dining', 'food_beverage'].includes(c)) return 'food';
        if (isCondo && c) return c;   // condo property tiles: category == tile.id
        return 'concierge';           // hotel concierge (sub-categories roll up to the Concierge tile)
    }
    return null;
};
// ONE icon truth for a request/tile: the resident's home tile, the progress bar
// and anything else that draws a category must use the same glyph, or the bar
// stops being recognisable as "my maintenance request". Keyed by the service id
// itemService() returns (which for property IS the tile id).
export const SERVICE_ICONS = {
    weather_event: CloudSun, waste_recycling: Recycle, parking: CircleParking,
    building: Building2, maintenance: Wrench, security: ShieldCheck,
    amenities: ConciergeBell, building_information: Info, concierge_front_desk: ConciergeBell,
    concierge: ConciergeBell, housekeeping: BroomIcon, food: UtensilsCrossed, valet: Car,
};
export const requestIcon = (it, isCondo) => (
    SERVICE_ICONS[itemService(it, isCondo)]
    || (it?.kind === 'security' ? ShieldCheck : it?.kind === 'valet' ? Car
        : it?.kind === 'tip' ? Gift : ConciergeBell)
);
export const activityStatusColor = (it) => _statusColor(it);
// Most attention-worthy color per service (red > amber > green); cancelled/cleared ignored.
export const computeServiceStatus = (activity, clearedSet, isCondo) => {
    const rank = { red: 3, amber: 2, green: 1 };
    const map = {};
    (activity || []).forEach((it) => {
        if (clearedSet && clearedSet.has(it.id)) return;
        const svc = itemService(it, isCondo);
        if (!svc) return;
        const c = _statusColor(it);
        if (c === 'grey') return;
        if (!map[svc] || rank[c] > rank[map[svc]]) map[svc] = c;
    });
    return map;
};
// Per-portal "cleared from My Requests" set (view-only; never touches backend).
const _clearKey = (cardId) => `yulontap_req_cleared:${cardId}`;
export const readCleared = (cardId) => {
    try { return new Set(JSON.parse(localStorage.getItem(_clearKey(cardId)) || '[]')); } catch { return new Set(); }
};
export const writeCleared = (cardId, set) => {
    try { localStorage.setItem(_clearKey(cardId), JSON.stringify([...set])); } catch { /* private mode */ }
};

export const MessagesScreen = ({ tx, lang, api, cardId, token, onBack, surface, propertyType, terminology }) => {
    const [messages, setMessages] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [acking, setAcking] = useState(null);
    const isCondo = propertyType === 'condominium_association';
    const screenTitle = isCondo ? (terminology?.notices?.[lang] || tx.communityBoard) : tx.messages;

    useEffect(() => {
        let alive = true;
        const load = () => fetch(`${api}/api/assistant/${cardId}/messages?token=${encodeURIComponent(token)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { if (alive && d) setMessages(d.messages || []); })
            .catch(() => { if (alive) setMessages((p) => p || []); });
        load();
        // Near-real-time: poll every 5s so new communications appear without a
        // manual refresh (opened/acknowledged flags are server-persisted).
        const iv = setInterval(load, 5000);
        return () => { alive = false; clearInterval(iv); };
    }, [api, cardId, token]);

    const pick = (m, base) => (m[`${base}_i18n`] && m[`${base}_i18n`][lang]) || m[base];

    const open = (m) => {
        setOpenId(openId === m.id ? null : m.id);
        if (!m.opened_at) {
            m.opened_at = new Date().toISOString();
            fetch(`${api}/api/assistant/${cardId}/messages/${m.id}/opened`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
            }).catch(() => {});
        }
    };

    const acknowledge = async (m, e) => {
        e.stopPropagation();
        setAcking(m.id);
        try {
            const r = await fetch(`${api}/api/assistant/${cardId}/messages/${m.id}/acknowledge`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
            });
            if (r.ok) {
                m.acknowledged_at = new Date().toISOString();
                setMessages((prev) => (prev ? [...prev] : prev));
            }
        } catch { /* noop */ } finally { setAcking(null); }
    };

    return (
        <ScreenShell title={screenTitle} onBack={onBack} tx={tx} testId="assistant-messages-screen">
            {!messages && <Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" />}
            {messages && messages.length === 0 && (
                <p className="text-slate-400 text-sm text-center" data-testid="assistant-messages-empty">{tx.noMessages}</p>
            )}
            <div className="space-y-3">
                {(messages || []).map((m) => {
                    const dTitle = pick(m, 'title');
                    const dMsg = pick(m, 'message');
                    const catLabel = m.category_labels ? (m.category_labels[lang] || m.category_labels.en) : m.category;
                    const needsAck = m.require_ack && !m.acknowledged_at;
                    return (
                    <div key={m.id} role="button" tabIndex={0} onClick={() => open(m)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(m); } }}
                        className={`w-full text-left rounded-2xl p-4 border transition-colors cursor-pointer ${m.type === 'voucher' ? 'bg-amber-500/10 border-amber-400/30 hover:bg-amber-500/15' : `${surface || DEFAULT_SURFACE} hover:bg-white/10`}`}
                        data-testid={`assistant-message-${m.id}`}>
                        {m.category && (
                            <span className="inline-block text-[10px] font-bold uppercase tracking-wide text-sky-200 bg-sky-500/15 border border-sky-400/30 rounded-full px-2 py-0.5 mb-1.5"
                                data-testid={`assistant-message-category-${m.id}`}>{catLabel}</span>
                        )}
                        <div className="flex items-center gap-2">
                            {!m.opened_at && <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0" data-testid={`assistant-message-unread-${m.id}`} />}
                            {m.priority === 'priority' && (
                                <span className="text-[10px] font-bold uppercase text-rose-300 bg-rose-500/15 border border-rose-400/30 rounded-full px-2 py-0.5 shrink-0"
                                    data-testid={`assistant-message-priority-${m.id}`}>{tx.priorityBadge}</span>
                            )}
                            <p className="text-white font-semibold text-sm flex-1 min-w-0 truncate">
                                {dTitle || (m.type === 'voucher' ? tx.voucherGift : (m.channel === 'community_board' ? tx.communityBoard : tx.hotelMessage))}
                            </p>
                            {m.type === 'voucher' && m.voucher && (
                                <span className="text-amber-300 font-bold text-sm shrink-0">${Number(m.voucher.value).toFixed(0)}</span>
                            )}
                        </div>
                        {dMsg && <p className="text-slate-300 text-sm mt-1">{dMsg}</p>}
                        {(m.photo_ids || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3" data-testid={`assistant-message-photos-${m.id}`}>
                                {m.photo_ids.map((pid) => (
                                    <img key={pid} src={`${api}/api/assistant/${cardId}/notice-photo/${pid}?token=${encodeURIComponent(token)}`}
                                        alt="" loading="lazy"
                                        className="w-20 h-20 object-cover rounded-lg border border-white/10"
                                        data-testid={`assistant-message-photo-${pid}`} />
                                ))}
                            </div>
                        )}
                        {m.require_ack && (
                            <div className="mt-3">
                                {m.acknowledged_at ? (
                                    <p className="text-emerald-300 text-xs font-semibold" data-testid={`assistant-message-acked-${m.id}`}>✓ {tx.acknowledged}</p>
                                ) : (
                                    <button type="button" onClick={(e) => acknowledge(m, e)} disabled={acking === m.id}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors disabled:opacity-60"
                                        data-testid={`assistant-message-ack-btn-${m.id}`}>
                                        {acking === m.id && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                        {tx.acknowledge}
                                    </button>
                                )}
                            </div>
                        )}
                        {openId === m.id && m.type === 'voucher' && m.voucher_code && (
                            <div className="mt-3">
                                {m.voucher?.redeemed ? (
                                    <p className="text-emerald-300 text-xs font-semibold" data-testid={`assistant-voucher-redeemed-${m.id}`}>{tx.voucherRedeemed}</p>
                                ) : (
                                    <a href={`${api}/api/vouchers/image/${m.voucher_code}`} target="_blank" rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 text-sm font-bold transition-colors"
                                        data-testid={`assistant-voucher-view-${m.id}`}>
                                        {tx.viewVoucher} <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                )}
                                {m.voucher?.expires_at && !m.voucher?.redeemed && (
                                    <p className="text-slate-400 text-[11px] mt-2">{tx.voucherExpires} {String(m.voucher.expires_at).slice(0, 10)}</p>
                                )}
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>
        </ScreenShell>
    );
};


// ─── Chunk 1: Guest Activity — READ-ONLY chronological view of the guest's
// existing records (security/concierge/valet requests + tips). No new records.
const _fmtActTime = (iso) => {
    try {
        return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
};

// FIX 1 — human, trilingual relative timestamp from the EXISTING request
// created_at. Just now / N min ago / N hours ago / Today, HH:MM / Yesterday,
// HH:MM / older localized date. No new timestamp system.
const _relTime = (iso, tx) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffMin < 1) return tx.timeJustNow || 'Just now';
    if (diffMin < 60) return (tx.timeMinAgo || '{n} min ago').replace('{n}', diffMin);
    const diffHr = Math.floor(diffMin / 60);
    const sameDay = d.toDateString() === now.toDateString();
    const yest = new Date(now); yest.setDate(now.getDate() - 1);
    const isYesterday = d.toDateString() === yest.toDateString();
    if (diffHr < 6 && sameDay) return ((diffHr === 1 ? tx.timeHourAgo : tx.timeHoursAgo) || '{n} h ago').replace('{n}', diffHr);
    if (sameDay) return `${tx.timeToday || 'Today'}, ${time}`;
    if (isYesterday) return `${tx.timeYesterday || 'Yesterday'}, ${time}`;
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// FIX 2 — map the REAL backend request state to the Command Center colour model
// (RED open → AMBER acknowledged → GREEN resolved). Purely derived; never a
// separate frontend status. Cancelled follows existing behaviour (neutral).
const _statusColor = (it) => {
    const s = (it.status || '').toLowerCase();
    if (s === 'cancelled' || s === 'canceled') return 'grey';
    if (it.stage === 'resolved' || s === 'resolved' || s === 'delivered' || s === 'redeemed') return 'green';
    if (it.stage === 'received') return 'amber';
    return 'red';
};
const _DOT_CLS = { red: 'bg-red-500', amber: 'bg-amber-400', green: 'bg-emerald-500', grey: 'bg-slate-500' };
const _statusWord = (tx, c) => (
    c === 'green' ? (tx.statusResolved || 'Resolved')
        : c === 'amber' ? (tx.statusHandling || 'Being handled')
            : c === 'grey' ? (tx.statusCancelled || 'Cancelled')
                : (tx.statusOpen || 'Open'));
const StatusDot = ({ color, tx, testId }) => (
    <span data-testid={testId} data-status={color} title={_statusWord(tx, color)} aria-label={_statusWord(tx, color)}
        className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${_DOT_CLS[color] || 'bg-slate-500'}`} />
);

// The colour dot alone explained nothing, and its only explanation was a hover
// tooltip — which does not exist on a touchscreen. A guest could not tell what
// amber meant. This is the same derived state (_statusColor), drawn as three
// labelled steps with the request's OWN category icon, so it reads without a
// legend, without hover, and progresses live on the existing 5s poll.
// Cancelled is NOT a stage of this journey and keeps its own grey dot.
const _STEP_FILL = { red: 'bg-red-500', amber: 'bg-amber-400', green: 'bg-emerald-500' };
const _STEP_TEXT = { red: 'text-red-400', amber: 'text-amber-300', green: 'text-emerald-400' };
const RequestProgress = ({ it, tx, testId, isCondo, big = false, size }) => {
    const color = _statusColor(it);
    const reached = color === 'green' ? 3 : color === 'amber' ? 2 : 1;
    const Icon = requestIcon(it, isCondo);
    const labels = [tx.reqStepSent || 'Sent', tx.reqStepSeen || 'Seen', tx.reqStepFixed || 'Fixed'];
    const fill = _STEP_FILL[color] || 'bg-slate-600';
    // `size="sm"` is the compact home-summary variant ONLY. Activity screens
    // keep the default dimensions they already read correctly at.
    const sm = size === 'sm';
    const dim = big ? 'w-8 h-8' : sm ? 'w-5 h-5' : 'w-7 h-7';
    const glyph = big ? 'w-4 h-4' : sm ? 'w-3 h-3' : 'w-3.5 h-3.5';
    const stepText = sm ? 'text-[10px]' : 'text-[11px]';
    return (
        <div data-testid={testId} data-status={color} data-stage={reached} className="w-full"
            aria-label={`${_statusWord(tx, color)} — ${labels[reached - 1]}`}>
            <div className="flex items-center">
                {[1, 2, 3].map((n) => (
                    <div key={n} className={n === 1 ? 'flex items-center shrink-0' : 'flex items-center flex-1 min-w-0'}>
                        {n > 1 && <span aria-hidden="true" className={`h-0.5 flex-1 min-w-0 rounded-full ${n <= reached ? fill : 'bg-slate-700'}`} />}
                        <span className={`${dim} rounded-full flex items-center justify-center shrink-0 ${n <= reached ? fill : 'bg-slate-800 border border-slate-700'}`}>
                            <Icon className={`${glyph} ${n <= reached ? 'text-slate-950' : 'text-slate-500'}`} />
                        </span>
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-3 mt-1">
                {labels.map((l, i) => (
                    <span key={l} data-testid={`${testId}-step-${i + 1}`}
                        className={`font-semibold ${stepText} ${i === 0 ? 'text-left' : i === 1 ? 'text-center' : 'text-right'} ${i + 1 <= reached ? (_STEP_TEXT[color] || 'text-slate-300') : 'text-slate-500'}`}>
                        {l}
                    </span>
                ))}
            </div>
        </div>
    );
};

const _actStageLabel = (tx, s) => (
    s === 'received' ? tx.shieldReceived : s === 'resolved' ? tx.shieldResolved : tx.shieldInProgress
);
const _actKindLabel = (tx, k) => (k === 'tip' ? tx.activityTipWord : (tx[k] || k));
const _actMoney = (tx, it) => (
    it.tip != null ? `$${Number(it.tip).toFixed(2)} ${tx.activityTipWord}` : tx.activityNoCharge
);

const ActivityDetail = ({ it, tx, onClose, isCondo }) => {
    const Row = ({ label, value, testId }) => (
        <div className="flex justify-between items-baseline gap-6 py-2 border-b border-white/5" data-testid={testId}>
            <span className="text-slate-400 text-xs shrink-0 pr-3">{label}</span>
            <span className="text-slate-100 text-xs text-right pl-3">{value ?? tx.activityNoCharge}</span>
        </div>
    );
    const who = it.resolved_by || it.received_by || (it.kind === 'tip' ? _actKindLabel(tx, it.title) : null);
    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 sm:p-4"
            data-testid="activity-detail-panel" onClick={onClose}>
            <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-t-2xl sm:rounded-2xl max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-slate-900 flex items-center justify-between p-4 border-b border-white/10 z-10">
                    <p className="text-white font-semibold" data-testid="activity-detail-title">{tx.activityDetailTitle}</p>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="activity-detail-close">✕</button>
                </div>
                <div className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-white font-bold">{_actKindLabel(tx, it.kind === 'tip' ? 'tip' : it.kind)}</span>
                        <span className="ml-auto text-xs font-semibold text-slate-300">{_statusWord(tx, _statusColor(it))}</span>
                    </div>
                    {_statusColor(it) === 'grey' ? (
                        <div className="flex items-center gap-2 mb-4">
                            <StatusDot color="grey" tx={tx} testId="activity-detail-status-dot" />
                            <span className="text-xs font-semibold text-slate-400">{_statusWord(tx, 'grey')}</span>
                        </div>
                    ) : (
                        <div className="mb-4 px-1">
                            <RequestProgress it={it} tx={tx} isCondo={isCondo} big testId="activity-detail-status-dot" />
                        </div>
                    )}
                    {(it.title || it.message) && (
                        <p className="text-slate-100 text-sm mb-3 bg-white/5 border border-white/10 rounded-lg p-2.5 capitalize">
                            {[it.title, it.message].filter(Boolean).join(' — ')}
                        </p>
                    )}
                    <Row label={tx.activityWhen} value={_relTime(it.created_at, tx)} testId="ad-when" />
                    {it.room_number && <Row label={tx.room} value={it.room_number} />}
                    <Row label={tx.activityWho} value={who || tx.activityNotYet} testId="ad-who" />
                    <Row label={tx.activityStatus} value={_statusWord(tx, _statusColor(it))} testId="ad-status" />
                    <Row label={tx.activityAmount} value={_actMoney(tx, it)} testId="ad-amount" />
                </div>
            </div>
        </div>
    );
};

// ─── Home-page summary of the resident/guest's OWN active requests.
// Same data (/activity), same 5s cadence, and the SAME RequestProgress bar as
// "My Requests" — reused, not re-implemented, so the two can never disagree.
// Resolved / cancelled / cleared are excluded: this section is about what is
// still owed to them. Renders nothing when there is nothing open.
export const HomeRequestsSummary = ({ tx, api, cardId, token, isCondo = false, surface, tileLabels }) => {
    const [items, setItems] = useState(null);
    const [detail, setDetail] = useState(null);
    const [cleared] = useState(() => readCleared(cardId));
    useEffect(() => {
        if (!cardId || !token) return;
        let alive = true;
        const load = async () => {
            try {
                const r = await fetch(`${api}/api/assistant/${cardId}/activity?token=${encodeURIComponent(token)}`);
                if (!alive || !r.ok) return;
                const arr = (await r.json()).activity || [];
                setItems(arr);
                setDetail((prev) => (prev ? (arr.find((x) => x.id === prev.id) || prev) : prev));
            } catch { /* keep the last good list */ }
        };
        load();
        const iv = setInterval(load, 5000);
        return () => { alive = false; clearInterval(iv); };
    }, [api, cardId, token]);

    // Active = still needs the guest's attention (red/amber). A request the
    // team JUST closed also belongs here for a few minutes: the guest (and, in
    // a demo, the presenter switching back from Security) has to SEE the status
    // land. After that it lives in Activity, where history belongs.
    const justClosed = (it) => {
        const ts = it.resolved_at || it.completed_at || it.updated_at;
        if (!ts) return false;
        const age = Date.now() - new Date(ts).getTime();
        return age >= 0 && age < 15 * 60 * 1000;
    };
    const active = (items || [])
        .filter((it) => !cleared.has(it.id)
            && (['red', 'amber'].includes(_statusColor(it)) || justClosed(it)))
        .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
        .slice(0, 3);
    if (!active.length) return null;
    return (
        <section className="mb-5" data-testid="home-requests-summary">
            <p className="text-slate-400 text-xs font-semibold uppercase tracking-widest mb-2.5"
                data-testid="home-requests-label">{tx.homeReqTitle}</p>
            <div className="space-y-2">
                {active.map((it) => (
                    <button key={it.id} type="button" onClick={() => setDetail(it)}
                        className={`${surface || 'bg-white/5 border border-white/10'} w-full text-left rounded-2xl p-2.5 hover:bg-white/10 transition-colors`}
                        data-testid={`home-request-${it.id}`}>
                        <div className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                            <span className="text-slate-400 text-xs">{_relTime(it.created_at, tx)}</span>
                            {/* The department/tile the resident actually filed under.
                                `it.kind` is the COLLECTION the row lives in
                                (concierge/security), so on a property a maintenance
                                request read "Concierge". The property tile label for
                                it.category is the real name. */}
                            <span className="text-xs font-semibold text-slate-300 truncate"
                                data-testid={`home-request-kind-${it.id}`}>· {
                                    (isCondo && tileLabels && tileLabels[it.category])
                                    || _actKindLabel(tx, (isCondo && itemService(it, isCondo)) || it.kind)
                                }</span>
                            <ChevronRight className="w-4 h-4 text-slate-500 ml-auto shrink-0" />
                        </div>
                        {(it.message || it.title) && (
                            <p className="text-white text-sm mt-1.5 capitalize truncate">
                                {it.message || String(it.title).replace(/_/g, ' ')}
                            </p>
                        )}
                        <div className="mt-2.5">
                            <RequestProgress it={it} tx={tx} isCondo={isCondo} size="sm" testId={`home-request-status-${it.id}`} />
                        </div>
                    </button>
                ))}
            </div>
            {detail && <ActivityDetail it={detail} tx={tx} isCondo={isCondo} onClose={() => setDetail(null)} />}
        </section>
    );
};

const _actItemTitle = (tx, title) => {
    if (typeof title === 'string' && title.startsWith('pool_')) {
        return (tx.poolCat && tx.poolCat[title.slice(5)]) || title.slice(5);
    }
    return title;
};

export const ActivityScreen = ({ tx, api, cardId, token, onBack, surface, isCondo = false }) => {
    const [items, setItems] = useState(null);
    const [detail, setDetail] = useState(null);
    const [cleared, setCleared] = useState(() => readCleared(cardId));
    const clearOne = (id) => { const n = new Set(cleared); n.add(id); setCleared(n); writeCleared(cardId, n); };
    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const r = await fetch(`${api}/api/assistant/${cardId}/activity?token=${encodeURIComponent(token)}`);
                if (!alive) return;
                if (r.ok) {
                    const arr = (await r.json()).activity || [];
                    setItems(arr);
                    // Live-sync an OPEN detail panel from the same refreshed data
                    // (same source of truth) so its RED→AMBER→GREEN updates without
                    // closing/reopening. No extra fetch — reuses /activity.
                    setDetail((prev) => (prev ? (arr.find((x) => x.id === prev.id) || prev) : prev));
                } else setItems((prev) => prev || []);
            } catch { if (alive) setItems((prev) => prev || []); }
        };
        load();
        // Live status refresh (reuses the existing safe polling approach — no
        // websockets). Manager RED→AMBER→GREEN transitions surface automatically.
        const iv = setInterval(load, 5000);
        return () => { alive = false; clearInterval(iv); };
    }, [api, cardId, token]);

    const kindColor = { security: 'text-rose-300', concierge: 'text-amber-300', valet: 'text-indigo-300', tip: 'text-emerald-300' };
    const visible = (items || []).filter((it) => !cleared.has(it.id));
    return (
        <ScreenShell title={tx.activityTitle} onBack={onBack} tx={tx} testId="assistant-activity-screen">
            {!items && <Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" />}
            {items && visible.length > 0 && (
                <p className="flex items-start gap-2 text-slate-400 text-xs mb-3" data-testid="activity-legend">
                    <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>{tx.reqProgressLegend}</span>
                </p>
            )}
            {items && visible.length === 0 && (
                <p className="text-slate-400 text-sm text-center py-6" data-testid="assistant-activity-empty">{tx.activityEmpty}</p>
            )}
            <div className="space-y-2">
                {visible.map((it) => (
                    <div key={it.id} className="relative">
                        <button type="button" onClick={() => setDetail(it)}
                            className={`${surface || 'bg-white/5 border border-white/10'} w-full text-left rounded-2xl p-3.5 pr-10 hover:bg-white/10 transition-colors`}
                            data-testid={`activity-item-${it.id}`}>
                            <div className="flex items-center gap-2">
                                <Clock className="w-3.5 h-3.5 text-slate-500" />
                                <span className="text-slate-400 text-xs" data-testid={`activity-time-${it.id}`}>{_relTime(it.created_at, tx)}</span>
                                <span className={`text-xs font-semibold ${kindColor[it.kind] || 'text-slate-300'}`}>· {_actKindLabel(tx, it.kind)}</span>
                                <span className="ml-auto text-[11px] font-semibold text-slate-300">{_statusWord(tx, _statusColor(it))}</span>
                            </div>
                            {(it.title || it.message) && (
                                <p className="text-white text-sm mt-1.5 capitalize">{[_actItemTitle(tx, it.title), it.message].filter(Boolean).join(' — ')}</p>
                            )}
                            {_statusColor(it) === 'grey' ? (
                                <div className="flex items-center gap-2 mt-2">
                                    <StatusDot color="grey" tx={tx} testId={`activity-status-${it.id}`} />
                                    <span className="text-[11px] font-semibold text-slate-400">{_statusWord(tx, 'grey')}</span>
                                </div>
                            ) : (
                                <div className="mt-2.5">
                                    <RequestProgress it={it} tx={tx} isCondo={isCondo} testId={`activity-status-${it.id}`} />
                                </div>
                            )}
                            <p className={`text-xs mt-2 font-semibold ${it.tip != null ? 'text-emerald-300' : 'text-slate-500'}`}>{_actMoney(tx, it)}</p>
                        </button>
                        <button type="button" onClick={() => clearOne(it.id)} title={tx.activityClear} aria-label={tx.activityClear}
                            className="absolute top-2 right-2 text-slate-500 hover:text-rose-300 p-1.5 leading-none text-sm" data-testid={`activity-clear-${it.id}`}>✕</button>
                    </div>
                ))}
            </div>
            {detail && <ActivityDetail it={detail} tx={tx} isCondo={isCondo} onClose={() => setDetail(null)} />}
        </ScreenShell>
    );
};


// ─── Chunk 2: Offers & Gift Cards — surfaces the EXISTING Voucher Engine to the
// guest (via hotel messages carrying a voucher_code). Read-only presentation.
const _fmtOfferDate = (iso) => {
    try { return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ''; }
};

const OfferDetail = ({ o, tx, onClose }) => (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 sm:p-4"
        data-testid="offer-detail-panel" onClick={onClose}>
        <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-t-2xl sm:rounded-2xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-slate-900 flex items-center justify-between p-4 border-b border-white/10 z-10">
                <p className="text-white font-semibold" data-testid="offer-detail-title">{tx.offersTitle}</p>
                <button type="button" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="offer-detail-close">✕</button>
            </div>
            <div className="p-4 text-center">
                <Gift className="w-10 h-10 text-emerald-400 mx-auto" />
                <p className="text-white font-bold text-lg mt-3">{o.title}</p>
                {o.value != null && <p className="text-emerald-300 text-2xl font-extrabold mt-1">${Number(o.value).toFixed(2)}</p>}
                {o.description && <p className="text-slate-300 text-sm mt-2">{o.description}</p>}
                <div className="mt-4 bg-white/5 border border-white/10 rounded-xl p-3">
                    <p className="text-slate-400 text-[11px] uppercase tracking-widest">{tx.offerCode}</p>
                    <p className="text-white text-xl font-mono font-bold tracking-wider mt-1" data-testid="offer-code">{o.code}</p>
                </div>
                {o.expires_at && <p className="text-slate-400 text-xs mt-3">{tx.offerExpires}: {_fmtOfferDate(o.expires_at)}</p>}
                <p className="text-slate-500 text-xs mt-2">{tx.offerRedeemHint}</p>
            </div>
        </div>
    </div>
);

export const OffersScreen = ({ tx, api, cardId, token, onBack, surface }) => {
    const [offers, setOffers] = useState(null);
    const [detail, setDetail] = useState(null);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await fetch(`${api}/api/assistant/${cardId}/offers?token=${encodeURIComponent(token)}`);
                if (!alive) return;
                setOffers(r.ok ? ((await r.json()).offers || []) : []);
            } catch { if (alive) setOffers([]); }
        })();
        return () => { alive = false; };
    }, [api, cardId, token]);
    return (
        <ScreenShell title={tx.offersTitle} onBack={onBack} tx={tx} testId="assistant-offers-screen">
            {!offers && <Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" />}
            {offers && offers.length === 0 && (
                <p className="text-slate-400 text-sm text-center py-6" data-testid="assistant-offers-empty">{tx.offersEmpty}</p>
            )}
            <div className="space-y-3">
                {(offers || []).map((o) => (
                    <button key={o.id} type="button" onClick={() => setDetail(o)}
                        className={`${surface || 'bg-white/5 border border-white/10'} w-full text-left rounded-2xl p-4 hover:bg-white/10 transition-colors flex items-center gap-3`}
                        data-testid={`offer-item-${o.id}`}>
                        <Gift className="w-7 h-7 text-emerald-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                            <p className="text-white font-semibold text-sm">{o.title}</p>
                            {o.description && <p className="text-slate-400 text-xs mt-0.5 truncate">{o.description}</p>}
                        </div>
                        {o.value != null && <span className="text-emerald-300 font-bold text-sm shrink-0">${Number(o.value).toFixed(2)}</span>}
                    </button>
                ))}
            </div>
            {detail && <OfferDetail o={detail} tx={tx} onClose={() => setDetail(null)} />}
        </ScreenShell>
    );
};
